import React from 'react';
import { Helmet } from 'react-helmet';

const SITE_NAME = 'Labcat.ai';
const SITE_URL = 'https://labcat.ai';
const DEFAULT_IMAGE = `${SITE_URL}/logo512.png`;

// Per-route SEO configuration
const SEO_CONFIG = {
  '/': {
    title: 'Labcat.ai - AI-Powered Academic PDF Reader & Research Assistant',
    description: 'Read, analyze, and chat with academic papers using AI. Translate research documents, polish academic writing, and track PubMed keywords — all in one platform. Free to try.',
    keywords: 'AI PDF reader, academic paper reader, research assistant, AI document analysis, PubMed tracker, academic translation, text polishing, scientific paper chat, literature review tool, 科研工具, 论文阅读器',
    canonical: SITE_URL + '/',
  },
  '/translation': {
    title: 'Academic Paper Translation - AI-Powered | Labcat.ai',
    description: 'Translate research papers and academic documents with AI. Supports English, Chinese, Spanish, French, German, Japanese, Korean and more. Context-aware scientific terminology.',
    keywords: 'academic translation, research paper translation, scientific document translator, AI translation, 学术翻译, paper translator, multi-language research',
    canonical: SITE_URL + '/translation',
  },
  '/verify-email': {
    title: 'Verify Your Email | Labcat.ai',
    description: 'Complete your Labcat.ai registration by verifying your email address.',
    keywords: 'verify email, Labcat.ai registration',
    canonical: SITE_URL + '/verify-email',
    noindex: true,
  },
  '/reset-password': {
    title: 'Reset Password | Labcat.ai',
    description: 'Reset your Labcat.ai account password.',
    keywords: 'reset password, Labcat.ai account',
    canonical: SITE_URL + '/reset-password',
    noindex: true,
  },
  '/payment-success': {
    title: 'Payment Successful | Labcat.ai',
    description: 'Your payment was successful. Tokens have been added to your account.',
    keywords: '',
    canonical: SITE_URL + '/payment-success',
    noindex: true,
  },
  '/payment-cancel': {
    title: 'Payment Cancelled | Labcat.ai',
    description: 'Your payment was cancelled. No charges were made.',
    keywords: '',
    canonical: SITE_URL + '/payment-cancel',
    noindex: true,
  },
};

/**
 * SEO component that sets per-route meta tags using react-helmet.
 * Usage: <SEO path={location.pathname} />
 */
export default function SEO({ path, customTitle, customDescription }) {
  // Normalize path: strip trailing slashes, default to '/'
  const normalizedPath = path === '/' ? '/' : path.replace(/\/+$/, '');
  const config = SEO_CONFIG[normalizedPath] || SEO_CONFIG['/'];

  const title = customTitle || config.title;
  const description = customDescription || config.description;
  const canonical = config.canonical || `${SITE_URL}${normalizedPath}`;
  const robots = config.noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      {config.keywords && <meta name="keywords" content={config.keywords} />}
      <meta name="robots" content={robots} />
      <link rel="canonical" href={canonical} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={DEFAULT_IMAGE} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_US" />
      <meta property="og:locale:alternate" content="zh_CN" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonical} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={DEFAULT_IMAGE} />

      {/* Hreflang for multilingual */}
      <link rel="alternate" hreflang="en" href={canonical} />
      <link rel="alternate" hreflang="zh" href={canonical} />
      <link rel="alternate" hreflang="x-default" href={canonical} />
    </Helmet>
  );
}

export { SEO_CONFIG, SITE_URL, SITE_NAME };
